import React, { Children, cloneElement, isValidElement, memo, useContext } from 'react';
import type { MemoExoticComponent, PropsWithChildren, ReactNode } from 'react';

import { LightNodeRendererContext } from './LightRootRenderer.tsx';

export const LightNodeRenderer: MemoExoticComponent<
	({ children, renderIndex, ...props }: LightNodeRendererProps) => ReactNode
> = memo(({ children, renderIndex, ...props }: LightNodeRendererProps): ReactNode => {
	const shouldRenderNode = useContext(LightNodeRendererContext);
	const shouldRender = shouldRenderNode?.(renderIndex) ?? true;

	if (!shouldRender) {
		return null;
	}

	// Some node components add props to their children, outside of `serializeFragment`. Pass such props through:
	if (Object.keys(props).length) {
		return <PassPropsThrough {...props}>{children}</PassPropsThrough>;
	}

	return children;
});
LightNodeRenderer.displayName = 'LightNodeRenderer';

// Memoized apart from the context read, so revealing a batch does not re-render every visible row.
const PassPropsThrough = memo(
	({ children, ...props }: PropsWithChildren): ReactNode =>
		Children.map(children, (child) => (isValidElement(child) ? cloneElement(child, props) : child)),
);
PassPropsThrough.displayName = 'PassPropsThrough';

type LightNodeRendererProps = PropsWithChildren<{
	renderIndex: number;
}>;
